import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { TagLabel } from 'features/timeSeries/experiment/TagLabel'
import { FC, ReactNode, useMemo, useState } from 'react'
import styled from 'styled-components'
import { RolodexEntryType, SensorEntryWrapper } from 'types'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const Row = styled.div`
  display: flex;
  gap: 0.35rem;
  align-items: center;
`

const Input = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  font-size: 0.72rem;
  max-width: 100%;
`

const ChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Remove = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
  padding: 0;
  font-size: 0.85rem;
  line-height: 1;
`

const Suggestions = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 10rem;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
`

const Suggestion = styled.li`
  padding: 0.3rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.forecast.background2};
  }
`

const Hint = styled.div`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const short = (id: string) => id.split('-').slice(1).join('-') || id

export interface TagPickerProps {
  siteId: string
  label: ReactNode
  value: string[]
  onChange: (ids: string[]) => void
  multi?: boolean
  exclude?: string[]
}

export const TagPicker: FC<TagPickerProps> = ({
  siteId,
  label,
  value,
  onChange,
  multi = false,
  exclude = [],
}) => {
  const [query, setQuery] = useState('')
  const { data: wrappers } = useGetRolodexEntriesByLoadOptions<
    SensorEntryWrapper[]
  >({
    type: [RolodexEntryType.DATA_POINTS],
    tags: { site: [siteId] },
    limit: 10000,
  })

  const options = useMemo(() => {
    const excludeSet = new Set([...exclude, ...value])
    const q = query.trim().toLowerCase()
    const rows: { id: string; label: string }[] = []
    for (const wrapper of wrappers ?? []) {
      const id = wrapper.entry.id
      if (excludeSet.has(id)) continue
      const data = wrapper.entry.data as { name?: string; pi_tag?: string }
      const labelText = data?.pi_tag || data?.name || short(id)
      const hay = `${id} ${labelText}`.toLowerCase()
      if (q && !hay.includes(q)) continue
      rows.push({ id, label: labelText })
      if (rows.length >= 40) break
    }
    return rows
  }, [wrappers, query, exclude, value])

  const pick = (id: string) => {
    if (multi) {
      onChange([...value, id])
    } else {
      onChange([id])
    }
    setQuery('')
  }

  const remove = (id: string) => {
    onChange(value.filter((v) => v !== id))
  }

  return (
    <Wrap>
      <Hint>{label}</Hint>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
        {value.map((id) => (
          <Chip key={id}>
            <TagLabel tagId={id}>
              <ChipText>{short(id)}</ChipText>
            </TagLabel>
            <Remove
              type='button'
              onClick={() => remove(id)}
              aria-label='Remove'>
              ×
            </Remove>
          </Chip>
        ))}
      </div>
      {(multi || value.length === 0) && (
        <>
          <Row>
            <Input
              value={query}
              placeholder='Search tags…'
              onChange={(e) => setQuery(e.target.value)}
            />
          </Row>
          {query.trim() && (
            <Suggestions>
              {options.length === 0 ? (
                <Suggestion style={{ cursor: 'default' }}>
                  No matches
                </Suggestion>
              ) : (
                options.map((opt) => (
                  <Suggestion key={opt.id} onClick={() => pick(opt.id)}>
                    <TagLabel tagId={opt.id}>
                      <span>
                        {opt.label}
                        <Hint
                          style={{ display: 'inline', marginLeft: '0.4rem' }}>
                          {short(opt.id)}
                        </Hint>
                      </span>
                    </TagLabel>
                  </Suggestion>
                ))
              )}
            </Suggestions>
          )}
        </>
      )}
    </Wrap>
  )
}
